import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import apolloLogger from 'apollo-link-logger';

import { ONE_SEC_MS } from '@glass/common/modules/dates/constants';

import ApolloLinkTimeout from '@glass/shared/modules/apollo/ApolloLinkTimeout';
import createHttpLink from '@glass/shared/modules/apollo/createHttpLink';
import onErrorLink from '@glass/shared/modules/apollo/onErrorLink';

let apolloContentfulClient = null;

const create = (options) => {
  const { token, spaceId, environmentId, timeout = 10 * ONE_SEC_MS } = options;

  if (!token) {
    throw new Error('token reqd');
  }

  if (!spaceId) {
    throw new Error('spaceId reqd');
  }

  const links = [...(__DEV__ ? [apolloLogger] : []), onErrorLink, new ApolloLinkTimeout(timeout)];

  const headers = {
    authorization: `Bearer ${token}`,
  };

  const uri = `https://graphql.contentful.com/content/v1/spaces/${spaceId}${
    environmentId ? `/environments/${environmentId}` : ''
  }`;

  links.push(createHttpLink({ uri, headers, credentials: 'omit', ...options }));

  const link = ApolloLink.from(links);

  // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
  return new ApolloClient({
    link,
    cache: new InMemoryCache().restore({}),
    queryDeduplication: true,
  });
};

const createContentfulClient = ({
  spaceId = process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID,
  environmentId = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT_ID,
  preview = false,
  timeout,
} = {}) => {
  const options = {
    spaceId,
    environmentId,
    timeout,
    token: preview
      ? process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN
      : process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN,
  };

  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (__SERVER__) {
    return create(options);
  }

  // Reuse client on the client-side
  if (!apolloContentfulClient) {
    apolloContentfulClient = create(options);
  }

  return apolloContentfulClient;
};

export default createContentfulClient;
